import { useActiveCirclePacks } from '@/hooks/use-packs';
import { usePrograms } from '@/hooks/use-programs';
import { MediaGuideMetadata } from '@/utils/media/types';
import { useMemo } from 'react';
import { QueueSource, QueueSourceKind } from '../../types';

export interface WarmAdjacentTracksProps {
    sessions: MediaGuideMetadata[];
    currentSessionIndex: number;
    next: (index: number) => number | null;
    previous: (index: number) => number | null;
    queueSource: QueueSource;
    /** Pack class of a program source; a playlist source maps its own per program. */
    packClass?: string;
}

/**
 * Prefetches the program and pack of the next and previous tracks in queue order,
 * so a skip doesn't wait on those requests before the new track can load. The
 * playing track's own program and pack are left to the active program provider.
 */
export function useWarmAdjacentTracks({
    sessions,
    currentSessionIndex,
    next,
    previous,
    queueSource,
    packClass,
}: WarmAdjacentTracksProps) {
    const { programIds, packClasses } = useMemo(() => {
        const packClassFor = (programId: string) =>
            queueSource.kind === QueueSourceKind.Playlist
                ? queueSource.packClassByProgramId[programId]
                : packClass;
        const playingProgramId = sessions[currentSessionIndex]?.programId;
        if (!playingProgramId) {
            return { programIds: [], packClasses: [] };
        }
        const playingPackClass = packClassFor(playingProgramId);
        // The playing program is already loaded, and warming it again would put its
        // id in a second key that every progress update invalidates and refetches.
        const adjacentProgramIds = [
            next(currentSessionIndex),
            previous(currentSessionIndex),
        ]
            .map((index) => (index === null ? undefined : sessions[index]))
            .map((guide) => guide?.programId)
            .filter(
                (programId): programId is string =>
                    !!programId && programId !== playingProgramId
            );
        const uniqueProgramIds = Array.from(new Set(adjacentProgramIds));
        return {
            programIds: uniqueProgramIds,
            packClasses: Array.from(
                new Set(
                    uniqueProgramIds
                        .map(packClassFor)
                        .filter(
                            (packClass): packClass is string =>
                                !!packClass && packClass !== playingPackClass
                        )
                )
            ),
        };
    }, [currentSessionIndex, next, previous, sessions, queueSource, packClass]);

    usePrograms(programIds);
    useActiveCirclePacks(packClasses);
}
